import { useRef, useEffect, useState, useCallback } from 'react';
import { CloseIcon } from './icons';
import styles from './SearchBar.module.css';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  debounceMs?: number;
}

const isMac = typeof navigator !== 'undefined' &&
  (navigator.platform?.startsWith('Mac') || (navigator as any).userAgentData?.platform === 'macOS');
const shortcutLabel = isMac ? '\u2318K' : 'Ctrl+K';

export function SearchBar({ value, onChange, debounceMs = 150 }: SearchBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [local, setLocal] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Sync external value
  useEffect(() => setLocal(value), [value]);

  const handleChange = useCallback(
    (v: string) => {
      setLocal(v);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => onChange(v), debounceMs);
    },
    [onChange, debounceMs],
  );

  // "/" and Cmd+K keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === '/' && !isEditing()) {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape' && document.activeElement === inputRef.current) {
        handleChange('');
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [handleChange]);

  return (
    <div className={styles.container}>
      <input
        ref={inputRef}
        type="text"
        className={styles.input}
        aria-label="Search bookmarks"
        placeholder={`Search bookmarks... ( / or ${shortcutLabel} )`}
        value={local}
        onChange={(e) => handleChange(e.target.value)}
      />
      {local && (
        <button className={styles.clear} onClick={() => handleChange('')} aria-label="Clear search">
          <CloseIcon size={14} />
        </button>
      )}
    </div>
  );
}

function isEditing(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || (el as HTMLElement).isContentEditable;
}
